<template>
  <div id="topbar" class="navbar-custom px-0">
    <div class="container-fluid pr-0 d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center flex-shrink-0">
        <div class="logo-box text-center">
          <router-link class="logo flex-shrink-0" to="/">
            <span class="logo-lg">
              <img alt height="28" src="@assets/images/athlisis-logo.svg"/>
            </span>
            <span class="logo-sm tw-mr-3">
              <img alt height="28" src="@assets/images/athlisis-logo-sm.svg"/>
            </span>
          </router-link>
        </div>

        <!-- below 992px the nav collapses into a floating menu sheet -->
        <b-collapse id="navigation" v-model="menuOpen">
          <ul class="navigation-menu">
            <li v-for="item in menu" :key="item.url" :class="{ 'has-submenu': item.subMenu }">
              <!-- in the collapsed menu a section row opens its sub-pages instead of navigating -->
              <router-link :to="item.url" :class="{ 'is-active': isMenuItemActive(item) }" tag="a" :data-testid="item.testid"
                           :event="item.subMenu && isCompact ? [] : 'click'"
                           :aria-expanded="item.subMenu && isCompact ? String(openSubmenu === item.url) : null"
                           @click.native="item.subMenu && isCompact && toggleSubmenu(item)"
              >
                <app-icon :name="item.icon" :size="16"/>
                <span>{{ item.name }}</span>
                <app-icon v-if="item.subMenu" name="chevron-down" :size="18"
                          :class="{ 'tw-rotate-180': openSubmenu === item.url }"
                          class="d-lg-none tw-ml-auto tw-text-primary-ink tw-transition-transform"
                />
              </router-link>
              <ul v-if="item.subMenu" :class="{ open: openSubmenu === item.url }" class="submenu megamenu">
                <li class="w-100">
                  <ul>
                    <li v-for="sub in item.subMenu" :key="sub.key || sub.url">
                      <router-link v-if="sub.url" :to="sub.url" tag="a"><i :class="sub.icon"></i>{{ sub.name }}</router-link>
                      <a v-else class="tw-cursor-pointer" :data-testid="`topbar-${sub.key}`" @click="runFromMenu(sub)">
                        <i :class="sub.icon"></i>{{ sub.name }}
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>

          <!-- on phones the secondary tools leave the bar for the menu -->
          <div class="d-md-none tw-mt-1 tw-pt-1 tw-border-0 tw-border-t tw-border-solid tw-border-slate-200">
            <a v-for="action in phoneMenuActions" :key="action.key"
               class="tw-flex tw-items-center tw-gap-2.5 tw-p-3 tw-text-sm tw-tracking-wide tw-text-primary-ink tw-rounded-[10px] tw-cursor-pointer hover:tw-bg-light-bg hover:tw-text-primary hover:tw-no-underline"
               @click="runFromMenu(action)"
            >
              <span v-if="action.brand" class="topbar-brand-icon"/>
              <app-icon v-else :name="action.icon" :size="18"/>
              {{ action.label }}
            </a>
          </div>
        </b-collapse>
      </div>

      <div v-if="menuOpen" class="d-lg-none tw-fixed tw-inset-x-0 tw-bottom-0 tw-top-[70px] tw-z-[1] tw-bg-gray-700/20"
           @click="closeMenu"
      />

      <ul class="list-unstyled topnav-menu d-flex align-items-center justify-content-end mb-0">
        <li class="d-none d-sm-block tw-relative">
          <div class="app-search">
            <div class="app-search-box">
              <div class="input-group">
                <div class="input-group-prepend">
                  <button class="btn" tabindex="-1" @click="openPalette()">
                    <app-icon name="search" :size="16" class="tw-text-primary-ink"/>
                  </button>
                </div>
                <!-- readonly: the field only opens the palette, and b-modal hands focus back to it on close -->
                <b-form-input id="search-athletes" :placeholder="$t('searchPlaceholder')" class="form-control" type="text"
                              readonly @click="openPalette()" @keydown.enter.prevent="openPalette()"
                />
              </div>
            </div>
          </div>
        </li>

        <li v-if="$route.name" class="nav-link dropdown-toggle">
          <language-switcher/>
        </li>

        <li v-for="action in toolbarActions" :key="action.key"
            v-b-tooltip.bottom="action.label" :data-testid="`toolbar-${action.key}`"
            :class="[cls.ICON_BUTTON, { [cls.ACTIVE]: action.active, 'tw-hidden md:tw-flex': action.phoneHidden, 'min-[576px]:tw-hidden': action.phoneOnly }]"
            @click="action.onClick"
        >
          <span v-if="action.brand" class="topbar-brand-icon"/>
          <app-icon v-else :name="action.icon"/>
          <b-badge v-if="action.badge" data-testid="toolbar-badge" :class="cls.BADGE" class="rounded-circle" pill variant="danger">{{ action.badge }}</b-badge>
        </li>

        <notifications-item/>

        <b-nav-item-dropdown id="avatar-menu" ref="avatar-dropdown" class="tw-group flex-shrink-0" menu-class="profile-menu tw-w-[235px]"
                             :toggle-class="[cls.AVATAR_TOGGLE, cls.DROPDOWN_OPEN]" right
        >
          <template slot="button-content">
            <person-avatar :person="user" fallback="silhouette" size="md"/>
            <app-icon name="chevron-down" :size="14" class="d-none d-sm-block tw-text-primary-ink"/>
          </template>

          <li v-if="user && user.institution" class="tw-px-4 tw-py-2">
            <student-institution-badge/>
          </li>
          <b-dropdown-divider v-if="user && user.institution"/>

          <b-dropdown-item to="/user">
            <i class="fe-user"/>{{ $t('topbar.account') }}
          </b-dropdown-item>
          <b-dropdown-item @click="triggerActiveAthletesModal()">
            <i class="fe-bar-chart-2"/>{{ $t('pages.athletes.activity.title') }}
          </b-dropdown-item>
          <b-dropdown-item @click="$store.dispatch('settings/openSettingsModal')">
            <i class="fe-settings"/>{{ $t('topbar.settings.title') }}
          </b-dropdown-item>
          <b-dropdown-divider/>
          <b-dropdown-item to="/logout" variant="danger">
            <i class="fe-log-out"/>{{ $t('topbar.logout') }}
          </b-dropdown-item>
        </b-nav-item-dropdown>

        <li class="d-lg-none tw-mr-2">
          <a :class="[cls.ICON_BUTTON, { [cls.ACTIVE]: menuOpen }]" :aria-expanded="String(menuOpen)" :aria-label="$t('topbar.menu')"
             data-testid="toolbar-menu-button"
             @click="menuOpen = !menuOpen"
          >
            <app-icon :name="menuOpen ? 'x' : 'menu-2'"/>
          </a>
        </li>
      </ul>
    </div>

    <b-modal id="tools-modal" ref="tools-modal" hide-footer size="xl" :title="$t('topbar.scienceTools')">
      <tools-base/>
    </b-modal>

    <b-modal id="pathisis-modal" ref="pathisis-modal" hide-footer scrollable size="xl" static title="Pathisis">
      <pathisis-base/>
    </b-modal>

    <b-modal id="create-client" ref="create-client" hide-footer size="md" :title="$t('topbar.athletes.quickAdd')">
      <NewCustomerModal @clientCreated="$refs['create-client'].hide()"/>
    </b-modal>

    <educational-gated-b-modal ref="athletes-popup" :title="$t('pages.athletes.activity.title')" hide-footer size="lg" scrollable
                               @hidden="triggerActiveAthletesModal(false)"
    >
      <athlete-statistics-modal v-if="statsModalOpened"/>
    </educational-gated-b-modal>

    <command-palette ref="palette"/>
  </div>
</template>
<script>
import {authComputed} from '@state/helpers'
import {mapState} from "vuex";
import toolsBase from '@components/tools/tools-base'
import NewCustomerModal from "@components/global/NewCustomerModal";
import pathisisBase from "@components/pathisis/pathisis-base"
import LanguageSwitcher from "@components/global/LanguageSwitcher.vue";
import NotificationsItem from "@components/top-bar/NotificationsItem.vue";
import PersonAvatar from "@components/global/PersonAvatar.vue";
import StudentInstitutionBadge from "@components/global/StudentInstitutionBadge.vue";
import AppIcon from "@components/icons/AppIcon.vue";
import * as toolbarClasses from "@components/top-bar/toolbarClasses";
import {openMarketplaceSSO} from "@utils/marketplaceBridge";
import {blurToggleAfterPointerClose} from "@utils/dropdownFocus";
import {useRecentChats} from "@src/queries/chat";

export default {
  components: {
    StudentInstitutionBadge,
    AppIcon,
    PersonAvatar,
    NotificationsItem,
    AthleteStatisticsModal: () => import(/* webpackPrefetch: true */ "@components/modals/AthleteStatisticsModal.vue"),
    CommandPalette: () => import(/* webpackChunkName: "command-palette" */ "@components/command-palette/CommandPalette.vue"),
    LanguageSwitcher, toolsBase, NewCustomerModal, pathisisBase
  },

  setup() {
    const {unreadCount} = useRecentChats({poll: true});
    return {unreadChats: unreadCount};
  },

  data() {
    return {
      cls: Object.freeze({...toolbarClasses}),
      // slot vnodes are created even while the modal is closed, which would fetch the chunk at startup
      statsModalOpened: false,
      menuOpen: false,
      // url of the section whose sub-pages are expanded in the collapsed menu
      openSubmenu: null,
      isCompact: false,
      compactQuery: null,
      menu: [
        {
          name: this.$t('topbar.dashboard'),
          icon: 'layout-dashboard',
          url: '/',
          testid: 'nav-home',
        },
        {
          name: this.$t('topbar.athletes.title'),
          icon: 'user',
          url: '/athletes',
          testid: 'nav-athletes',
          subMenu: [
            {name: this.$t('topbar.athletes.all'), icon: 'fe-list', url: '/athletes'},
            {name: this.$t('topbar.athletes.statistics'), icon: 'fe-pie-chart', url: '/athletes/statistics'},
            {key: 'activity', name: this.$t('pages.athletes.activity.title'), icon: 'fe-bar-chart-2', onClick: () => this.triggerActiveAthletesModal()},
            {name: this.$t('topbar.athletes.new'), icon: 'fe-plus-circle', url: '/athletes/add'},
            {key: 'quick-add', name: this.$t('topbar.athletes.quickAdd'), icon: 'fe-plus-square', onClick: () => this.$refs['create-client'].show()},
          ]
        },
        {
          name: this.$t('topbar.foods'),
          icon: 'apple',
          url: '/foods'
        },
        {
          name: this.$t('topbar.dietPlans.title'),
          icon: 'file-text',
          url: '/diet-plans',
          testid: 'nav-diet-plans',
          subMenu: [
            {name: this.$t('topbar.dietPlans.all'), icon: 'fe-list', url: '/diet-plans'},
            {name: this.$t('topbar.dietPlans.templates'), icon: 'fe-bookmark', url: '/meals'},
            {name: this.$t('topbar.dietPlans.statistics'), icon: 'fe-pie-chart', url: '/diet-plans/statistics'},
            {name: this.$t('topbar.dietPlans.printTemplates'), icon: 'icon-printer', url: '/user/print-templates'},
            {name: this.$t('topbar.dietPlans.noteTemplates'), icon: 'icon-notebook', url: '/user/note-templates'},
            {name: this.$t('topbar.dietPlans.recipes'), icon: 'icon-book-open', url: '/recipes'},
          ]
        },
      ],
    }
  },

  computed: {
    ...authComputed,
    ...mapState({
      user: state => state.auth.currentUser,
      showActiveAthletesModal: state => state.athlete.showActiveAthletesModal,
    }),
    // one list drives the bar's icon buttons and, for `phoneMenu` ones, the rows of the phone menu
    toolbarActions() {
      const routeName = this.$route.name || '';
      const actions = [
        {
          // the bar's search field is hidden below 576px
          key: 'search', icon: 'search', label: this.$t('commandPalette.title'), phoneOnly: true,
          onClick: () => this.openPalette(),
        },
        {
          key: 'marketplace', brand: true, label: this.$t('topbar.marketplace'), phoneMenu: true,
          hidden: this.isOfflineVersion || this.isEducational,
          onClick: () => openMarketplaceSSO().catch(() => this.emitErrorNotification({duration: 5000})),
        },
        {
          key: 'cloud', icon: 'cloud-upload', label: this.$t('topbar.aCloud'), phoneMenu: true,
          active: routeName.includes('cloud'),
          onClick: () => this.$router.push({name: 'cloud'}),
        },
        {
          key: 'chat', icon: 'message-2', label: this.$t('topbar.chat'),
          active: routeName.includes('chat'), badge: this.unreadChats,
          onClick: () => this.$router.push({name: 'chat'}),
        },
        {
          key: 'tools', icon: 'settings', label: this.$t('topbar.scienceTools'), phoneMenu: true,
          onClick: () => this.$refs['tools-modal'].show(),
        },
        {
          // Pathisis isn't offered on phones
          key: 'pathisis', icon: 'heartbeat', label: this.$t('topbar.pathisis'), phoneHidden: true,
          onClick: () => this.$refs['pathisis-modal'].show(),
        },
        {
          key: 'calendar', icon: 'calendar', label: this.$t('topbar.calendar'),
          active: routeName === 'calendar',
          onClick: () => this.$router.push({name: 'calendar'}),
        },
      ];
      return actions
        .filter(action => !action.hidden)
        .map(action => ({...action, phoneHidden: action.phoneHidden || action.phoneMenu}));
    },
    phoneMenuActions() {
      return this.toolbarActions.filter(action => action.phoneMenu);
    },
  },

  mounted() {
    blurToggleAfterPointerClose(this.$refs['avatar-dropdown']);
    // the breakpoint where the nav collapses into the menu sheet
    this.compactQuery = window.matchMedia('(max-width: 991.98px)');
    this.isCompact = this.compactQuery.matches;
    this.compactQuery.addEventListener('change', this.onCompactChange);
  },
  beforeDestroy() {
    this.compactQuery.removeEventListener('change', this.onCompactChange);
  },
  watch: {
    showActiveAthletesModal(val) {
      if (val) {
        this.statsModalOpened = true;
        this.$refs['athletes-popup'].show();
      } else this.$refs['athletes-popup'].hide();
    },
    '$route.fullPath'() {
      this.closeMenu();
    },
    menuOpen(open) {
      if (!open) return;
      const current = this.menu.find(item => item.subMenu && this.isMenuItemActive(item));
      this.openSubmenu = current ? current.url : null;
    },
  },

  methods: {
    closeMenu() {
      this.menuOpen = false;
    },
    openPalette() {
      this.closeMenu();
      if (this.$refs.palette) this.$refs.palette.open();
    },
    runFromMenu(action) {
      this.closeMenu();
      action.onClick();
    },
    onCompactChange(e) {
      this.isCompact = e.matches;
    },
    toggleSubmenu(item) {
      this.openSubmenu = this.openSubmenu === item.url ? null : item.url;
    },
    // a section stays lit on its sub-pages (e.g. a client's card under Πελάτες)
    isMenuItemActive(item) {
      const path = this.$route.path;
      if (item.url === '/') return path === '/';
      const urls = [item.url, ...(item.subMenu || []).filter(sub => sub.url).map(sub => sub.url)];
      return urls.some(url => path === url || path.startsWith(`${url}/`));
    },
  },
}
</script>
